import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DirectMessages } from "@/components/chat/DirectMessages";
import { Hash, Users, Send, Search, Plus, Pin, Smile } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface Channel {
  id: string;
  name: string;
  type: "public" | "private";
  description?: string;
  member_count: number;
}

interface Message {
  id: string;
  content: string;
  created_at: string;
  user: {
    id: string;
    full_name: string;
    email: string;
  };
  pinned: boolean;
  mentions: string[];
}

export default function TeamChat() {
  const { user, orgId } = useAuth();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [activeChannel, setActiveChannel] = useState<Channel | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [orgUsers, setOrgUsers] = useState<any[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");

  useEffect(() => {
    if (orgId) {
      loadChannels();
      loadOrgUsers();
    }
  }, [orgId]);

  useEffect(() => {
    if (activeChannel) {
      loadMessages();
      subscribeToMessages();
    }
  }, [activeChannel]);

  const loadChannels = async () => {
    // For now, create a default general channel
    setChannels([
      {
        id: "general",
        name: "general",
        type: "public",
        description: "Company-wide announcements and discussions",
        member_count: 10,
      },
      {
        id: "projects",
        name: "projects",
        type: "public",
        description: "Project updates and coordination",
        member_count: 8,
      },
      {
        id: "random",
        name: "random",
        type: "public",
        description: "Off-topic conversations",
        member_count: 12,
      },
    ]);
    setActiveChannel({
      id: "general",
      name: "general",
      type: "public",
      description: "Company-wide announcements and discussions",
      member_count: 10,
    });
  };

  const loadOrgUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("org_id", orgId)
        .neq("id", user?.id);

      if (error) throw error;
      setOrgUsers(data || []);
    } catch (error: any) {
      console.error("Error loading org users:", error);
    }
  };

  const loadMessages = async () => {
    if (!activeChannel || !orgId) return;

    try {
      // Load messages from comments table filtered by a special channel reference
      const { data, error } = await supabase
        .from("comments")
        .select(`
          id,
          content,
          created_at,
          pinned,
          user_id,
          profiles!comments_user_id_fkey (
            id,
            full_name,
            email
          )
        `)
        .eq("org_id", orgId)
        .is("job_id", null)
        .is("lead_id", null)
        .order("created_at", { ascending: true })
        .limit(100);

      if (error) throw error;

      const formattedMessages = (data || []).map((msg: any) => ({
        id: msg.id,
        content: msg.content,
        created_at: msg.created_at,
        pinned: msg.pinned,
        user: {
          id: msg.profiles?.id || msg.user_id,
          full_name: msg.profiles?.full_name || "Unknown User",
          email: msg.profiles?.email || "",
        },
        mentions: [],
      }));

      setMessages(formattedMessages);
    } catch (error: any) {
      console.error("Error loading messages:", error);
    }
  };

  const subscribeToMessages = () => {
    if (!activeChannel || !orgId) return;

    const channel = supabase
      .channel(`channel-${activeChannel.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "comments",
          filter: `org_id=eq.${orgId}`,
        },
        () => {
          loadMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user || !orgId || !activeChannel) return;

    try {
      const { error } = await supabase.from("comments").insert({
        org_id: orgId,
        user_id: user.id,
        content: newMessage,
        job_id: null,
        lead_id: null,
        pinned: false,
      });

      if (error) throw error;

      setNewMessage("");
      setShowMentions(false);
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  const handleTextChange = (text: string) => {
    setNewMessage(text);

    // Check for @ mentions
    const lastAtIndex = text.lastIndexOf("@");
    if (lastAtIndex !== -1 && lastAtIndex === text.length - 1) {
      setShowMentions(true);
      setMentionSearch("");
    } else if (lastAtIndex !== -1 && text.length > lastAtIndex + 1) {
      const searchText = text.substring(lastAtIndex + 1);
      if (!searchText.includes(" ")) {
        setShowMentions(true);
        setMentionSearch(searchText);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (userName: string, userId: string) => {
    const lastAtIndex = newMessage.lastIndexOf("@");
    const beforeMention = newMessage.substring(0, lastAtIndex);
    setNewMessage(`${beforeMention}@${userName} `);
    setShowMentions(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const filteredUsers = orgUsers.filter((u) =>
    u.full_name?.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  return (
        <Tabs defaultValue="channels" className="w-full">
          <TabsList>
            <TabsTrigger value="channels">Channels</TabsTrigger>
            <TabsTrigger value="direct">Direct Messages</TabsTrigger>
          </TabsList>
          <TabsContent value="channels">
      <div className="h-screen flex bg-background">
        {/* Sidebar - Channels */}
        <div className="w-64 border-r border-border bg-card flex flex-col">
          <div className="p-4 border-b border-border">
            <h2 className="font-bold text-lg">Team Chat</h2>
            <p className="text-xs text-muted-foreground">Organization-wide</p>
          </div>

          <ScrollArea className="flex-1 px-3 py-4">
            <div className="space-y-1">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase">
                  Channels
                </span>
                <Button variant="ghost" size="icon" className="h-5 w-5">
                  <Plus className="h-3 w-3" />
                </Button>
              </div>

              {channels.map((channel) => (
                <button
                  key={channel.id}
                  onClick={() => setActiveChannel(channel)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                    activeChannel?.id === channel.id
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  <Hash className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{channel.name}</span>
                </button>
              ))}
            </div>

            <Separator className="my-4" />

            <div className="space-y-1">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase">
                  Direct Messages
                </span>
              </div>
              <div className="px-2 py-2 text-xs text-muted-foreground">
                Switch to Direct Messages tab above
              </div>
            </div>
          </ScrollArea>

          <div className="p-3 border-t border-border">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {user?.email ? getInitials(user.email) : "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.email?.split("@")[0]}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Channel Header */}
          {activeChannel && (
            <div className="h-14 border-b border-border px-4 flex items-center justify-between bg-card">
              <div className="flex items-center gap-2">
                <Hash className="h-5 w-5 text-muted-foreground" />
                <div>
                  <h3 className="font-semibold">{activeChannel.name}</h3>
                  {activeChannel.description && (
                    <p className="text-xs text-muted-foreground">
                      {activeChannel.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon">
                  <Users className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon">
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4 max-w-4xl mx-auto">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <Hash className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    No messages yet. Start the conversation!
                  </p>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className="flex gap-3 hover:bg-accent/50 -mx-2 px-2 py-1 rounded group"
                  >
                    <Avatar className="h-9 w-9 mt-0.5">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {getInitials(message.user.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold text-sm">
                          {message.user.full_name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(message.created_at), {
                            addSuffix: true,
                          })}
                        </span>
                        {message.pinned && (
                          <Badge variant="secondary" className="text-xs">
                            <Pin className="h-3 w-3 mr-1" />
                            Pinned
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm mt-1 whitespace-pre-wrap">
                        {message.content}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Message Input */}
          <div className="p-4 border-t border-border bg-card">
            <div className="max-w-4xl mx-auto relative">
              {showMentions && filteredUsers.length > 0 && (
                <Card className="absolute bottom-full mb-2 w-full max-h-48 overflow-auto">
                  {filteredUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => insertMention(u.full_name, u.id)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-accent transition-colors"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                          {getInitials(u.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <p className="text-sm font-medium">{u.full_name}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </button>
                  ))}
                </Card>
              )}

              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <Input
                    value={newMessage}
                    onChange={(e) => handleTextChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder={`Message #${activeChannel?.name || "channel"}`}
                    className="pr-10"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1 h-7 w-7"
                  >
                    <Smile className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  size="icon"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">
                  Enter
                </kbd>{" "}
                to send, use @ to mention teammates
              </p>
            </div>
          </div>
        </div>
        </div>
          </TabsContent>
          <TabsContent value="direct">
            {user && orgId && <DirectMessages orgId={orgId} currentUserId={user.id} />}
          </TabsContent>
        </Tabs>
  );
};
